import { isEmpty, isEmail, formatMobilePhone } from '@jjj/components';
import { isMobilePhone } from '../../../../helpers/validators';
import actions from './actions';
import { createReducer } from '../../../../helpers/reducer';
import { keyBy } from '../../../../helpers/core';
import { isISR, setDefaultCountryField } from '../../../../helpers/countriesHelpers';
import { setDefaultTimezoneField } from '../../../../helpers/dateTime';
import { t } from '../../../../i18n';

const initState = {
  user: null,
  editForm: null,
  loading: false,
  isResendingEmail: false,
  formInfo: {
    countries: [],
  },
  deleted: false,
  total: 0,
  filteredTotal: 0,
  filters: null,
};

const initialEditData = {
  role: null,
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  country: '',
  state: '',
  time_zone: '',
};

function validate(attr, value, params) {
  let error = '';

  if (attr === 'role') {
    if (isEmpty(value)) {
      error = t('dash.validation.select_a_field', { fieldName: t('dash.admin.users.columns.role_title') });
    }
  }

  if (!isISR() && attr === 'state') {
    if (isEmpty(value)) {
      error = t('dash.validation.select_a_field', { fieldName: t('dash.admin.users.columns.state_title') });
    }
  }

  if (attr === 'time_zone') {
    if (isEmpty(value)) {
      error = t('dash.validation.select_a_field', { fieldName: t('dash.admin.users.columns.timezone_title') });
    }
  }

  if (attr === 'first_name') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.admin.users.columns.first_name_title') });
    } else if (value.length >= 45) {
      error = t('dash.validation.max_length_error', { fieldName: t('dash.admin.users.columns.first_name_title') });
    }
  }

  if (attr === 'last_name') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.admin.users.columns.last_name_title') });
    } else if (value.length >= 45) {
      error = t('dash.validation.max_length_error', { fieldName: t('dash.admin.users.columns.last_name_title') });
    }
  }

  if (attr === 'country') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.admin.users.columns.country_title') });
    }
  }

  if (attr === 'email') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.admin.users.columns.email_title') });
    } else if (!isEmail(value)) {
      error = t('dash.validation.valid_error', { fieldName: t('dash.admin.users.columns.email_title') });
    }
  }

  if (attr === 'phone') {
    if (!isMobilePhone(formatMobilePhone(value), params?.phoneNumberCountry)) {
      error = t('dash.validation.valid_error', { fieldName: t('dash.admin.users.columns.phone_title') });
    }
  }

  return error;
}

function onLoad(state) {
  return {
    ...state,
    loading: true,
  };
}

function onLoadSuccess(state, { payload }) {
  const { data } = payload;
  const user = data;

  return {
    ...state,
    user,
    loading: false,
  };
}

function onClearStore() {
  return { ...initState };
}

function onEdit(state, { payload }) {
  const { user } = payload;
  const propsArray = Object.keys(user);
  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => {
      return {
        name: prop,
        value: user[prop] && Object.prototype.hasOwnProperty.call(user[prop], 'id') ? user[prop].id : user[prop],
        error: validate(prop, user[prop]),
        touched: false,
      };
    },
  );

  return {
    ...state,
    editForm,
  };
}

function onSaveSuccess(state, { payload }) {
  const { data } = payload;
  const user = data;

  return {
    ...state,
    user,
    editForm: null,
  };
}

function onSaveFail(state, { payload }) {
  const editForm = { ...state.editForm };

  // eslint-disable-next-line no-restricted-syntax
  for (const key in payload.data) {
    // eslint-disable-next-line no-prototype-builtins
    if (payload.data.hasOwnProperty(key) && editForm[key]) {
      // eslint-disable-next-line prefer-destructuring
      editForm[key].error = payload.data[key][0];
      editForm[key].touched = true;
    }
  }

  return { ...state, editForm };
}

function onDelete(state) {
  return {
    ...state,
    editForm: null,
  };
}

function onDeleteSuccess(state) {
  return {
    ...state,
    deleted: true,
  };
}

function onCreate(state) {
  const propsArray = Object.keys(initialEditData);
  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: initialEditData[prop],
      error: validate(prop, initialEditData[prop]),
      touched: false,
    }),
  );

  return {
    ...state,
    user: initialEditData,
    editForm: {
      ...editForm,
      country: setDefaultCountryField(),
      time_zone: setDefaultTimezoneField(),
    },
  };
}

function onChangeProperty(state, { payload }) {
  const { attr, value, params } = payload;
  const { editForm } = state;

  editForm[attr] = {
    value,
    error: validate(attr, value, params),
    name: attr,
    touched: true,
  };

  return {
    ...state,
    editForm: {
      ...editForm,
    },
  };
}

function onChangeProperties(state, { payload }) {
  const { attributes } = payload;
  const propsArray = Object.keys(attributes);

  const editFormProps = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: attributes[prop],
      error: validate(prop, attributes[prop]),
      touched: true,
    }),
  );

  const editForm = {
    ...state.editForm,
    ...editFormProps,
  };

  return {
    ...state,
    editForm,
  };
}

function onCancel(state) {
  return {
    ...state,
    editForm: null,
  };
}

function onTouch(state, { payload }) {
  const propsArray = Object.entries(payload.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [, value] = entry;

      return {
        ...value,
        touched: true,
      };
    },
  );

  return {
    ...state,
    editForm,
  };
}

function onLoadFormData(state, { payload }) {
  const { data } = payload;

  return {
    ...state,
    formInfo: { ...data },
  };
}

function onResendEmailStart(state) {
  return { ...state, isResendingEmail: true };
}

function onResendEmailSuccess(state) {
  return { ...state, isResendingEmail: false };
}

function onResendEmailError(state) {
  return { ...state, isResendingEmail: false };
}

export default createReducer(initState, {
  [actions.CANCEL]: onCancel,
  [actions.CHANGE_PROPERTIES]: onChangeProperties,
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.CLEAR_STORE]: onClearStore,
  [actions.CREATE]: onCreate,
  [actions.DELETE]: onDelete,
  [actions.DELETE_SUCCESS]: onDeleteSuccess,
  [actions.EDIT]: onEdit,
  [actions.LOAD]: onLoad,
  [actions.LOAD_FORM_DATA]: onLoadFormData,
  [actions.LOAD_SUCCESS]: onLoadSuccess,
  [actions.RESEND_EMAIL_START]: onResendEmailStart,
  [actions.RESEND_EMAIL_SUCCESS]: onResendEmailSuccess,
  [actions.RESEND_EMAIL_ERROR]: onResendEmailError,
  [actions.SAVE_FAIL]: onSaveFail,
  [actions.SAVE_SUCCESS]: onSaveSuccess,
  [actions.TOUCH]: onTouch,
});
